import { useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { ORGANISATION_DASHBOARD } from '@/config'
import { DashboardFilters } from '@/components/UI/Dashboard'
import UsageTrendsChart from '@/routes/Dashboard/pages/partials/UsageTrendsChart/UsageTrendsChart'
import MetricOverview from '@/components/UI/MetricOverview'
import { AgentSetupCompletion, AgentEngagement } from './components'
import './index.scss'
import { useQuery } from '@tanstack/react-query'
import { getOrganisationOverview } from '@/services/OrganisationDashboardService'
import { useGlobalContext } from '@/context/GlobalContext'

const OrganisationDashboard = () => {
  const { t } = useTranslation('translation')
  const history = useHistory()
  const location = useLocation()

  const [filtersData, setFiltersData] = useState({
    filter_from_date: null,
    filter_to_date: null,
    channel: null,
    dateParameters: {
      value: '30',
      label: t('Last 30 days'),
    },
  })
  const { currentOrganisation } = useGlobalContext()

  const { data: metricData, isLoading: isMetricLoading } = useQuery({
    queryKey: ['org-overview', filtersData, currentOrganisation?._id],
    queryFn: () => getOrganisationOverview(filtersData),
    enabled:
      !!currentOrganisation?._id &&
      !!(filtersData.filter_from_date && filtersData.filter_to_date),
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
  })

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    if (!params.get('agent')) {
      history.push(ORGANISATION_DASHBOARD)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const hasFilterUpdated = (e: any) => {
    setFiltersData({
      ...filtersData,
      ...e,
    })
  }

  return (
    <section className="dashboard-wrapper">
      {/*--------- Filters -----------*/}
      <DashboardFilters
        hasFilterUpdated={hasFilterUpdated}
        hideClasses
        isOrganisationDashboard
        hideChannels
      />

      <div className="tw-pt-4 tw-flex tw-flex-col tw-gap-4">
        {/*--------------Top Metrics----------- */}
        <MetricOverview
          isLoading={isMetricLoading}
          data={[
            {
              title: t('Total Agents'),
              value: metricData?.agent_count,
            },
            {
              title: t('Total Admins'),
              value: metricData?.admin_count,
            },
            {
              title: t('Total Learners'),
              value: metricData?.user_count,
            },
            {
              title: t('Unique Active Learners'),
              value: metricData?.active_user_count,
              showChartIcon: true,
            },
          ]}
        />

        {/*--------------Bottom Metrics----------- */}
        <MetricOverview
          isLoading={isMetricLoading}
          data={[
            {
              title: t('Total Questions Asked'),
              value: metricData?.question_count,
              showChartIcon: true,
            },
            {
              title: t('Questions Answered'),
              value: metricData?.answered_question_count,
              helperText: t(
                'Percentage of Number of Questions in which the AI Assistant was able to provide an answer.',
              ),
              showChartIcon: true,
            },
            {
              title: t('Quiz Submissions'),
              value: metricData?.quiz_submission_count,
              showChartIcon: true,
            },
            {
              title: t('Role Play Submissions'),
              value: metricData?.roleplay_submission_count,
              showChartIcon: true,
            },
          ]}
        />

        {/*--------------Usage Trends----------- */}
        <UsageTrendsChart filtersData={filtersData} isOrganisation />

        {/*------ Agent Setup Completion ---------- */}
        <AgentSetupCompletion filtersData={filtersData} />

        {/*------ Agent Engagement ---------- */}
        <AgentEngagement filtersData={filtersData} />
      </div>
    </section>
  )
}

export default OrganisationDashboard
